import { useEffect, useMemo, useRef, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  PauseCircle,
  RefreshCw,
  XCircle,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Progress } from "@/components/ui/progress"
import { Separator } from "@/components/ui/separator"

import {
  useCancelLibraryTreeBackfillMutation,
  useLibraryTreeBackfillStatusQuery,
  useRestartLibraryTreeBackfillMutation,
} from "../hooks/admin-queries"
import { adminQueryKeys } from "../hooks/admin-query-keys"

type FailedFile = { file: string; error: string }

export type LibraryTreeBackfillModalProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  libraryId: string | null
  libraryName: string | null
  jobId: string | null
  /** Called once when the server cleared a stale lock (job row missing). Parent should clear local job id. */
  onOrphanLockCleared?: () => void
  /** Fired when restart yields a new job id so the parent can swap its state. */
  onJobChanged?: (newJobId: string) => void
}

const TERMINAL_STATUSES = new Set(["completed", "completed_with_errors", "failed", "cancelled"])

function isTerminal(status: string): boolean {
  return TERMINAL_STATUSES.has(status)
}

function parseFailedFiles(raw: unknown): FailedFile[] {
  if (typeof raw !== "string" || raw.trim() === "") return []
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((x): x is Record<string, unknown> => !!x && typeof x === "object")
      .map((x) => ({
        file: String(x.file ?? ""),
        error: String(x.error ?? ""),
      }))
      .filter((x) => x.file.length > 0)
  } catch {
    return []
  }
}

function formatSecondsAgo(iso: string | null | undefined, now: number): string | null {
  if (!iso) return null
  const parsed = Date.parse(iso)
  if (!Number.isFinite(parsed)) return null
  const secs = Math.max(0, Math.round((now - parsed) / 1000))
  return `${secs}s`
}

export function LibraryTreeBackfillModal({
  isOpen,
  onOpenChange,
  libraryId,
  libraryName,
  jobId,
  onOrphanLockCleared,
  onJobChanged,
}: LibraryTreeBackfillModalProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const cancelMutation = useCancelLibraryTreeBackfillMutation()
  const restartMutation = useRestartLibraryTreeBackfillMutation()
  const [terminalNotice, setTerminalNotice] = useState<"orphan" | "not_found" | null>(null)
  const [cancelling, setCancelling] = useState(false)
  const terminalNotifyDoneRef = useRef(false)

  const statusQuery = useLibraryTreeBackfillStatusQuery(libraryId, jobId, isOpen)
  const data = statusQuery.data
  const job = data && "job" in data ? data.job : undefined
  const isStale = (data && "isStale" in data ? data.isStale : undefined) ?? false

  // Ticker so the "last update Ns ago" heartbeat line advances live without
  // needing its own server roundtrip.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!isOpen) return
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) {
      setTerminalNotice(null)
      setCancelling(false)
      terminalNotifyDoneRef.current = false
      return
    }
    const d = statusQuery.data
    let kind: "orphan" | "not_found" | null = null
    if (d && "orphanLockCleared" in d) {
      kind = "orphan"
    } else if (d && "jobNotFound" in d) {
      kind = "not_found"
    }
    if (!kind || !libraryId) return
    setTerminalNotice(kind)
    if (terminalNotifyDoneRef.current) return
    terminalNotifyDoneRef.current = true
    void queryClient.invalidateQueries({
      queryKey: adminQueryKeys.libraryTreeBackfillActiveAll(),
    })
    onOrphanLockCleared?.()
  }, [isOpen, libraryId, onOrphanLockCleared, queryClient, statusQuery.data])

  const filesTotal = Number(job?.filesTotal ?? 0)
  const processed = Number(job?.processed ?? 0)
  const failed = Number(job?.failed ?? 0)
  const skipped = Number(job?.skipped ?? 0)
  const succeeded = Number(job?.succeeded ?? 0)
  const failedOverflow = Number(job?.failedFilesOverflow ?? 0)
  const status = String(job?.status ?? "")
  const terminal = isTerminal(status)
  const lastHeartbeatAt = typeof job?.lastHeartbeatAt === "string" ? job.lastHeartbeatAt : null
  const heartbeatAgo = formatSecondsAgo(lastHeartbeatAt, now)

  // While the orchestrator is still streaming discovery, ``filesTotal`` is a
  // running "discovered" count — render an indeterminate bar until discovery
  // settles (heuristic: the server marks running but nothing's queued yet).
  const discovering = status === "pending" || (status === "running" && filesTotal === 0)
  const pct = useMemo(() => {
    if (!filesTotal || filesTotal <= 0) return 0
    return Math.max(0, Math.min(100, Math.round((processed / filesTotal) * 100)))
  }, [filesTotal, processed])

  const failedFiles = useMemo(() => parseFailedFiles(job?.failedFilesJson), [job?.failedFilesJson])

  // Reset the local "cancelling…" hold once the server flips out of running.
  useEffect(() => {
    if (!cancelling) return
    if (status === "cancel_requested" || status === "cancelled" || terminal) {
      setCancelling(false)
    }
  }, [cancelling, status, terminal])

  // Refresh the Trees coverage indicator once the job reaches a terminal
  // state — otherwise the row keeps showing the pre-backfill count until a
  // hard refresh drops the cached response.
  const coverageRefreshedRef = useRef(false)
  useEffect(() => {
    if (!isOpen) {
      coverageRefreshedRef.current = false
      return
    }
    if (!terminal || !libraryId || coverageRefreshedRef.current) return
    coverageRefreshedRef.current = true
    void queryClient.invalidateQueries({
      queryKey: adminQueryKeys.libraryTreeCoverage(libraryId),
    })
    void queryClient.invalidateQueries({
      queryKey: adminQueryKeys.libraryTreeCoverageAll(),
    })
    void queryClient.invalidateQueries({
      queryKey: adminQueryKeys.libraryTreeBackfillActiveAll(),
    })
  }, [isOpen, libraryId, queryClient, terminal])

  const statusIcon = (() => {
    if (terminalNotice) return <AlertCircle className="text-warning-ink size-4" />
    if (statusQuery.isLoading)
      return <Loader2 className="text-muted-foreground size-4 animate-spin" />
    if (status === "completed") return <CheckCircle2 className="text-primary size-4" />
    if (status === "completed_with_errors")
      return <AlertCircle className="text-warning-ink size-4" />
    if (status === "failed") return <XCircle className="text-destructive size-4" />
    if (status === "cancelled") return <PauseCircle className="text-muted-foreground size-4" />
    return <Loader2 className="text-muted-foreground size-4 animate-spin" />
  })()

  // Cancel is allowed any time the job is non-terminal and we actually have a
  // job id to target. This includes loading/errored poll states — users would
  // otherwise have no escape while the dialog is waiting on the server.
  const canCancel = Boolean(!terminalNotice && libraryId && jobId && !terminal)

  const handleCancel = () => {
    if (!libraryId || !jobId) return
    setCancelling(true)
    cancelMutation.mutate(
      { libraryId, jobId },
      {
        onError: (err: unknown) => {
          setCancelling(false)
          console.error("Failed to cancel tree backfill", err)
          toast.error(
            t(
              "libraries.treeBackfill.cancelFailed",
              "Failed to cancel. The job may already be stopping."
            )
          )
        },
      }
    )
  }

  const handleRetry = () => {
    if (!libraryId || !jobId) return
    restartMutation.mutate(
      { libraryId, jobId },
      {
        onSuccess: (res) => {
          onJobChanged?.(res.job_id)
          toast.success(t("libraries.treeBackfill.retryStarted", "Restarted backfill."))
        },
        onError: (err: unknown) => {
          console.error("Failed to restart tree backfill", err)
          toast.error(t("libraries.treeBackfill.retryFailed", "Failed to restart backfill."))
        },
      }
    )
  }

  const handleStopAndClose = () => {
    if (canCancel) handleCancel()
    onOpenChange(false)
  }

  const primaryTerminalButton = (() => {
    if (status === "failed") {
      return (
        <Button disabled={restartMutation.isPending} variant="default" onClick={handleRetry}>
          {restartMutation.isPending ? (
            <Loader2 className="mr-2 size-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 size-4" />
          )}
          {t("libraries.treeBackfill.retry", "Retry")}
        </Button>
      )
    }
    if (status === "completed") {
      // Brand-primary CTA — themable per DESIGN.md.
      return (
        <Button variant="default" onClick={() => onOpenChange(false)}>
          <CheckCircle2 className="mr-2 size-4" />
          {t("libraries.treeBackfill.done", "Done")}
        </Button>
      )
    }
    if (status === "completed_with_errors") {
      // Warning variant: keeps the partial-success state visually distinct from a clean Done.
      return (
        <Button
          className="bg-warning text-warning-foreground border-foreground hover:bg-warning/90 border"
          onClick={() => onOpenChange(false)}
        >
          <AlertCircle className="mr-2 size-4" />
          {t("libraries.treeBackfill.doneWithErrors", "Done (with errors)")}
        </Button>
      )
    }
    // cancelled / orphan / not_found → plain Close
    return (
      <Button variant="default" onClick={() => onOpenChange(false)}>
        {t("common.close")}
      </Button>
    )
  })()

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {statusIcon}
            {t("libraries.treeBackfill.title", { name: libraryName ?? libraryId ?? "" })}
          </DialogTitle>
          <DialogDescription>
            {jobId
              ? t("libraries.treeBackfill.jobId", { jobId })
              : t("libraries.treeBackfill.noJob")}
          </DialogDescription>
        </DialogHeader>

        {isStale ? (
          <div className="border-warning-ink bg-warning/30 text-warning-ink rounded-md border px-3 py-2 text-sm">
            {t("libraries.treeBackfill.staleHint")}
          </div>
        ) : null}

        {terminalNotice ? (
          <div className="border-warning-ink bg-warning/30 text-warning-ink rounded-md border px-3 py-2 text-sm">
            {terminalNotice === "orphan"
              ? t("libraries.treeBackfill.orphanLockCleared")
              : t("libraries.treeBackfill.jobNotFound")}
          </div>
        ) : null}

        {statusQuery.isError ? (
          <div className="border-destructive/30 bg-destructive/5 flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm">
            <div className="text-destructive">
              {t(
                "libraries.treeBackfill.pollError",
                "Couldn't reach the backend. The job may still be running."
              )}
            </div>
            <Button
              disabled={statusQuery.isFetching}
              size="sm"
              variant="outline"
              onClick={() => void statusQuery.refetch()}
            >
              {statusQuery.isFetching ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                t("libraries.treeBackfill.retryPoll", "Retry")
              )}
            </Button>
          </div>
        ) : null}

        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <div className="text-muted-foreground">
              {discovering
                ? t("libraries.treeBackfill.discovering", "Discovering files…")
                : t("libraries.treeBackfill.progress")}
            </div>
            <div className="font-medium tabular-nums">
              {filesTotal > 0 ? `${processed}/${filesTotal}` : `${processed}`}
            </div>
          </div>
          {discovering ? (
            // Indeterminate until discovery settles — the running ``filesTotal``
            // would otherwise make the bar snap backwards as more files arrive.
            <div className="bg-secondary relative h-2 w-full overflow-hidden rounded-full">
              <div className="bg-primary-ink absolute inset-y-0 left-0 w-1/3 animate-pulse rounded-full" />
            </div>
          ) : (
            <Progress value={pct} />
          )}
          <div className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 text-xs">
            <div className="tabular-nums">
              {t("libraries.treeBackfill.succeeded", { count: succeeded })}
            </div>
            <div className="tabular-nums">
              {t("libraries.treeBackfill.failed", { count: failed })}
            </div>
            <div className="tabular-nums">
              {t("libraries.treeBackfill.skipped", "Skipped: {{count}}", { count: skipped })}
            </div>
            <div className="tabular-nums">{t("libraries.treeBackfill.status", { status })}</div>
          </div>
          {!terminal && heartbeatAgo ? (
            <div className="text-muted-foreground text-xs">
              {t("libraries.treeBackfill.heartbeat", "Active · last update {{ago}} ago", {
                ago: heartbeatAgo,
              })}
            </div>
          ) : null}
        </div>

        {failedFiles.length > 0 ? (
          <>
            <Separator />
            <div className="space-y-2">
              <div className="text-sm font-medium">
                {t("libraries.treeBackfill.failedFilesTitle")}
              </div>
              <div className="max-h-48 overflow-auto rounded-md border p-2 text-xs">
                <ul className="space-y-1">
                  {failedFiles.slice(0, 50).map((f) => (
                    <li key={`${f.file}-${f.error}`} className="break-words">
                      <span className="font-medium">{f.file}</span>
                      {f.error ? <span className="text-muted-foreground"> — {f.error}</span> : null}
                    </li>
                  ))}
                </ul>
                {failedFiles.length > 50 ? (
                  <div className="text-muted-foreground mt-2">
                    {t("libraries.treeBackfill.failedFilesTruncated", {
                      count: failedFiles.length - 50,
                    })}
                  </div>
                ) : null}
                {failedOverflow > 0 ? (
                  <div className="text-muted-foreground mt-2 italic">
                    {t(
                      "libraries.treeBackfill.failedFilesOverflow",
                      "…and {{count}} more not shown",
                      {
                        count: failedOverflow,
                      }
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </>
        ) : null}

        <div className="flex items-center justify-end gap-2">
          {terminal || terminalNotice ? (
            primaryTerminalButton
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                {t("libraries.treeBackfill.runInBackground", "Run in background")}
              </Button>
              <Button
                disabled={!canCancel || cancelling || cancelMutation.isPending}
                variant="destructive"
                onClick={handleStopAndClose}
              >
                {cancelling || cancelMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    {t("libraries.treeBackfill.cancelling")}
                  </>
                ) : (
                  t("libraries.treeBackfill.stopAndClose", "Stop and close")
                )}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
